/* START OF FILE js/StorageManager.js */
// js/StorageManager.js

// No internal dependencies to import for the class definition itself

export default class StorageManager { // Add export default
  constructor() {
    const cached = localStorage.getItem('squdle-server-date');
    const isValidDate = cached && /^\d{4}-\d{2}-\d{2}$/.test(cached) && !isNaN(new Date(cached + 'T00:00:00Z').getTime());
    this.todayDate = isValidDate ? cached : new Date().toISOString().split('T')[0];
    this.init();
  }

  persistServerDate(date) {
    localStorage.setItem('squdle-server-date', date);
  }

  /**
   * Initialize the storage manager
   * Performs migration of legacy data if needed
   */
  init() {
    this.migrateLegacyData();
    this.logStorageState(); // Log storage state on initialization
    this.clearStaleData(); // Clear old data on initialization
  }

  saveGameState(puzzle, isHardMode, hasCompletedTodaysPuzzle, gridSize = 4, challengeToken = null) {
    // Don't save if puzzle object isn't provided or puzzle is marked completed
    if (!puzzle || typeof puzzle !== 'object' || hasCompletedTodaysPuzzle) {
      // console.log(`[StorageManager.saveGameState] Save skipped. Puzzle: ${!!puzzle}, Completed: ${hasCompletedTodaysPuzzle}`);
      return false;
    }

    // --- ADD VALIDATION BEFORE SAVING ---
    if (!puzzle.grid || puzzle.grid.length !== gridSize || !puzzle.grid[0] || puzzle.grid[0].length !== gridSize || puzzle.grid[0][0] === "" ||
        !puzzle.solvedGrid || puzzle.solvedGrid.length !== gridSize || !puzzle.solvedGrid[0] || puzzle.solvedGrid[0].length !== gridSize || puzzle.solvedGrid[0][0] === "") {
       console.error(`[StorageManager.saveGameState] Attempted to save invalid/empty grid state for ${gridSize}x${gridSize}. Aborting save.`);
       return false; // Prevent saving empty state
    }
    // --- END VALIDATION ---

    const puzzleState = {
      grid: puzzle.grid,
      solvedGrid: puzzle.solvedGrid,
      swapCount: puzzle.swapCount,
      seed: puzzle.seed,
      date: this.todayDate,
      gridSize: gridSize,
      isHardMode: isHardMode, // Save the hard mode status with the state
      completed: false, // Explicitly false when saving in-progress state
      hintUsed: puzzle.hintUsed || false, // Save hint status
      challengeToken: challengeToken, // Save challenge token for leaderboard eligibility
    };

    const stateKey = `squdle-current-state-${gridSize}x${gridSize}`;
    try {
      localStorage.setItem(stateKey, JSON.stringify(puzzleState));
      // console.log(`✅ Saved state for ${gridSize}x${gridSize}. HardMode: ${isHardMode}, Swaps: ${puzzle.swapCount}`);
      return true;
    } catch (error) {
      console.error('[StorageManager.saveGameState] Error saving game state:', error);
      return false;
    }
  }

  loadGameState(gridSize = 4) {
    const stateKey = `squdle-current-state-${gridSize}x${gridSize}`;

    try {
      const savedState = localStorage.getItem(stateKey);
      if (!savedState) {
        // console.log(`[StorageManager.loadGameState] No state found for key: ${stateKey}`);
        return null;
      }

      const state = JSON.parse(savedState);

      // Basic validation (structure, size, date)
      if (
        !state || typeof state !== 'object' ||
        !state.grid || !Array.isArray(state.grid) || state.grid.length !== gridSize || !state.grid[0] || state.grid[0].length !== gridSize ||
        !state.solvedGrid || !Array.isArray(state.solvedGrid) || state.solvedGrid.length !== gridSize || !state.solvedGrid[0] || state.solvedGrid[0].length !== gridSize ||
        typeof state.swapCount !== 'number' ||
        state.gridSize !== gridSize || state.date !== this.todayDate
      ) {
        console.warn(`[StorageManager.loadGameState] Invalid or stale state structure/date for ${gridSize}x${gridSize}. Clearing.`);
        this.clearGameState(gridSize);
        return null;
      }

      // --- GRID CONTENT VALIDATION ADDED ---
      // Check if the grids actually contain letters (check top-left cell as a proxy)
      if (state.grid[0][0] === "" || state.solvedGrid[0][0] === "") {
          console.warn(`[StorageManager.loadGameState] State for ${gridSize}x${gridSize} contains EMPTY grids. Clearing.`);
          this.clearGameState(gridSize);
          return null;
      }
      // --- END GRID CONTENT VALIDATION ---


      // Default missing properties if needed (e.g., from older versions)
      state.isHardMode = state.isHardMode || false;
      state.hintUsed = state.hintUsed || false;
      state.completed = state.completed || false;


      // console.log(`[StorageManager.loadGameState] Loading valid state for ${gridSize}x${gridSize}. Completed: ${state.completed}, HardMode: ${state.isHardMode}`);
      return state; // Return the full state object

    } catch (error) {
      console.error(`[StorageManager.loadGameState] Error loading game state for ${gridSize}x${gridSize}:`, error);
      this.clearGameState(gridSize); // Clear potentially corrupted state
      return null;
    }
  }

  /**
   * Saves the completed puzzle state, updates the completion flag,
   * and adds the score to the leaderboard if eligible.
   * @param {SqudlePuzzle} puzzle - The solved puzzle instance.
   * @param {boolean} isHardModeAtCompletion - Was hard mode active *at the moment* the puzzle was solved?
   * @param {number} [gridSize=4] - The size of the grid.
   * @param {string|null} [challengeToken=null] - Token for leaderboard submission.
   * @returns {boolean} True if the state was successfully saved.
   */
  saveCompletedPuzzle(puzzle, isHardModeAtCompletion, gridSize = 4, challengeToken = null) {
    if (!puzzle || typeof puzzle !== 'object') {
      console.error('[StorageManager.saveCompletedPuzzle] Invalid puzzle object provided.');
      return false;
    }

     // --- ADD VALIDATION BEFORE SAVING ---
     if (!puzzle.solvedGrid || puzzle.solvedGrid.length !== gridSize || !puzzle.solvedGrid[0] || puzzle.solvedGrid[0].length !== gridSize || puzzle.solvedGrid[0][0] === "") {
       console.error(`[StorageManager.saveCompletedPuzzle] Attempted to save invalid/empty solvedGrid state for ${gridSize}x${gridSize}. Aborting save.`);
       return false; // Prevent saving empty state
    }
    // --- END VALIDATION ---

    console.log(
      `[StorageManager.saveCompletedPuzzle] Saving completed puzzle. Size: ${gridSize}, Swaps: ${puzzle.swapCount}, HardModeAtCompletion: ${isHardModeAtCompletion}`
    );

    // Use a size-specific key for completion status flag
    const completionKey = `squdle-completed-${gridSize}x${gridSize}-${this.todayDate}`;
    localStorage.setItem(completionKey, 'true');

    // Update the state to reflect solved state and completion status
    const stateKey = `squdle-current-state-${gridSize}x${gridSize}`;
    const solvedPuzzleState = {
      grid: puzzle.solvedGrid, // Save solved grid in both places for completed state
      solvedGrid: puzzle.solvedGrid,
      swapCount: puzzle.swapCount,
      seed: puzzle.seed,
      date: this.todayDate,
      gridSize: gridSize,
      // Store HM status *at time of completion* within the saved completed state
      // This is mainly informational for the restored state display.
      isHardMode: isHardModeAtCompletion,
      completed: true,        // Mark as completed
      hintUsed: puzzle.hintUsed || false, // Save final hint status
    };

    let saveSuccess = false;
    try {
      localStorage.setItem(stateKey, JSON.stringify(solvedPuzzleState));
      console.log(`[StorageManager.saveCompletedPuzzle] Updated state for ${gridSize}x${gridSize} to reflect solved state.`);
      saveSuccess = true;
    } catch (error) {
      console.error(`[StorageManager.saveCompletedPuzzle] Error saving completed game state for ${gridSize}x${gridSize}:`, error);
      // Don't clear game state here, maybe just completion flag failed
      // this.clearGameState(gridSize);
      return false; // Return false if save failed
    }

    // --- Leaderboard Logic ---
    const MAX_SWAPS_FOR_SIZE = this.getMaxSwapsForGridSize(gridSize);
    let eligibleForLeaderboard = false;
    let leaderboardIsHardMode = false;

    if (isHardModeAtCompletion && puzzle.swapCount <= MAX_SWAPS_FOR_SIZE) {
      // Scenario B: Hard Mode win within limit
      eligibleForLeaderboard = true;
      leaderboardIsHardMode = true;
      console.log(`[StorageManager.saveCompletedPuzzle] Score eligible for leaderboard (Hard Mode Win).`);
    } else if (!isHardModeAtCompletion) {
      // Scenario A & E: Unlimited win (initial or switched before failure)
      eligibleForLeaderboard = true;
      leaderboardIsHardMode = false;
      console.log(`[StorageManager.saveCompletedPuzzle] Score eligible for leaderboard (Unlimited/Regular Mode Win).`);
    } else {
      // Scenario C: Hard Mode was true at completion, BUT swaps > MAX_SWAPS_FOR_SIZE
      console.log(`[StorageManager.saveCompletedPuzzle] Score NOT eligible for leaderboard (Completed after Hard Mode failure/limit exceeded).`);
      eligibleForLeaderboard = false;
    }

    if (eligibleForLeaderboard) {
      if (challengeToken) {
        this.updateLeaderboard(gridSize, puzzle.swapCount, leaderboardIsHardMode, challengeToken);
      } else {
          console.warn("[StorageManager.saveCompletedPuzzle] Eligible for leaderboard but missing challenge token. Skipping submission.");
      }
    }
    // --- End Leaderboard Logic ---


    // Reset the Hard Mode *preference* for this size after completion
    // This happens regardless of leaderboard eligibility.
    const currentSettings = this.loadSettings();
    const prefs = currentSettings.hardModePrefs || {};
    prefs[gridSize] = false; // Set preference for this size back to false
    this.saveSettings({ ...currentSettings, hardModePrefs: prefs });
    // console.log(`[StorageManager.saveCompletedPuzzle] Hard mode preference for ${gridSize}x${gridSize} reset after completion.`);

    return saveSuccess; // Return true if the state save itself was successful
  }

  async updateLeaderboard(gridSize, swapCount, isHardMode = false, challengeToken) {
    const LEADERBOARD_WORKER_URL = 'https://leaderboard.squdle.com';
    const rawName = localStorage.getItem('squdle-username');
    const NAME_PATTERN = /^[a-zA-Z0-9 _-]+$/;
    const playerName = (rawName && rawName.length <= 30 && NAME_PATTERN.test(rawName))
      ? rawName
      : `Player${Math.floor(Math.random() * 10000)}`;
    
    // Ensure the date is correct for the puzzle being submitted
    const dateStr = this.todayDate;
    const sizeKey = `${gridSize}x${gridSize}`;

    const payload = {
        playerName: playerName,
        score: swapCount,
        puzzleDate: dateStr,
        puzzleSize: sizeKey,
        isHardMode: isHardMode,
        challengeToken: challengeToken
    };

    console.log(`[StorageManager.updateLeaderboard] Submitting score to backend...`, payload);

    try {
        const response = await fetch(`${LEADERBOARD_WORKER_URL}/score`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        });

        if (response.ok) {
            console.log(`[StorageManager.updateLeaderboard] Score submitted successfully.`);
            // Optionally refresh local leaderboard cache or notify UI
            document.dispatchEvent(new CustomEvent('leaderboard-updated'));
        } else {
            const errorText = await response.text();
            console.error(`[StorageManager.updateLeaderboard] Failed to submit score: ${response.status} ${errorText}`);
        }
    } catch (error) {
        console.error(`[StorageManager.updateLeaderboard] Network error submitting score:`, error);
    }
  }

  async getLeaderboard(gridSize = 4, mode = null) {
    const LEADERBOARD_WORKER_URL = 'https://leaderboard.squdle.com';
    const dateStr = this.todayDate;
    const sizeKey = `${gridSize}x${gridSize}`;
    const modeParam = mode ? `?mode=${mode}` : '';

    try {
        const response = await fetch(`${LEADERBOARD_WORKER_URL}/leaderboard/${sizeKey}/${dateStr}${modeParam}`);
        if (response.ok) {
            const data = await response.json();
            // Map backend format to frontend expectation if needed
            // Backend returns: { size, date, scores: [{ playerName, score, isHardMode, timestamp }] }
            return data.scores || [];
        } else {
            console.warn(`[StorageManager.getLeaderboard] Failed to fetch leaderboard: ${response.status}`);
            return [];
        }
    } catch (error) {
        console.error(`[StorageManager.getLeaderboard] Network error fetching leaderboard:`, error);
        return [];
    }
  }

  clearGameState(gridSize = 4) {
    const stateKey = `squdle-current-state-${gridSize}x${gridSize}`;
    localStorage.removeItem(stateKey);
    console.log(`[StorageManager.clearGameState] Cleared state for key: ${stateKey}`); // More informative log
  }

  hasCompletedTodaysPuzzle(gridSize, date = this.todayDate) {
    const completionKey = `squdle-completed-${gridSize}x${gridSize}-${date}`;
    return localStorage.getItem(completionKey) === 'true';
  }

  recordPlayedToday() {
    localStorage.setItem('squdle-last-played', this.todayDate);
  }

  migrateLegacyData() {
    // Migrate generic settings to include hardModePrefs
    const genericSettingsKey = 'squdle-settings';
    const genericSettingsData = localStorage.getItem(genericSettingsKey);
    if (genericSettingsData) {
      try {
        const settings = JSON.parse(genericSettingsData);
        // If hardModePrefs doesn't exist, create it based on the old isHardMode
        if (typeof settings.hardModePrefs === 'undefined') {
          const oldGridSize = settings.gridSize || 4;
          const oldIsHardMode = settings.isHardMode || false;
          settings.hardModePrefs = {
            '4': oldGridSize === 4 ? oldIsHardMode : false,
            '5': oldGridSize === 5 ? oldIsHardMode : false,
          };
          // Remove the old top-level isHardMode setting
          delete settings.isHardMode;
          localStorage.setItem(genericSettingsKey, JSON.stringify(settings));
          console.log('[StorageManager.migrateLegacyData] Migrated legacy settings to use hardModePrefs.');
        }
      } catch (e) {
        console.error("[StorageManager.migrateLegacyData] Error migrating legacy settings:", e);
      }
    }
  }

  clearStaleData() {
    const keysToRemove = [];
    const currentIsoDate = this.todayDate; // Use the stored todayDate
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('squdle-')) {
        // Keep current settings, accessibility, and hide instructions pref
        if (key === 'squdle-settings' || key === 'squdle-accessibility') continue;
        if (key === 'squdle-device-id' || key === 'squdle-session' || key === 'squdle-telemetry-init' || key === 'squdle-telemetry-optout') continue;

        // Keep size-specific player stats (no date)
        if (key.match(/^squdle-player-stats-\dx\d$/)) {
          continue;
        }

        // Check for date-specific keys (state, completion, leaderboard)
        // Remove if the date doesn't match today's date
        const dateMatch = key.match(/(\d{4}-\d{2}-\d{2})$/);
        if (dateMatch && dateMatch[1] !== currentIsoDate) {
          keysToRemove.push(key);
          continue;
        }

        // Check for legacy keys to remove (if not already covered)
        if (key === 'squdle-completed-date' || key === 'squdle-last-swap-count' || key === 'squdle-last-played' || key === 'squdle-player-stats') {
          keysToRemove.push(key);
        }
      }
    }
    if (keysToRemove.length > 0) {
      console.log('[StorageManager.clearStaleData] Clearing stale data:', keysToRemove);
      keysToRemove.forEach((key) => {
        try { localStorage.removeItem(key); } catch (e) { console.error(`[StorageManager.clearStaleData] Failed to remove stale key ${key}:`, e); }
      });
    }
  }

  getMaxSwapsForGridSize(gridSize) {
    switch (parseInt(gridSize)) {
      case 4: return 21;
      case 5: return 30;
      case 6: return 40; // Assuming 6x6 might exist later
      default: return 21; // Default for unknown sizes
    }
  }

  /**
   * Saves game settings (grid size, hard mode preferences, instructions visibility).
   * @param {object} settings - The settings object to save.
   */
  saveSettings(settings) {
    try {
      const currentSettings = this.loadSettings() || {};
      // Ensure hardModePrefs exists and is an object before merging
      const currentPrefs = typeof currentSettings.hardModePrefs === 'object' && currentSettings.hardModePrefs !== null
        ? currentSettings.hardModePrefs : {};
      const newPrefs = typeof settings.hardModePrefs === 'object' && settings.hardModePrefs !== null
        ? settings.hardModePrefs : {};

      const mergedSettings = {
        ...currentSettings,
        ...settings,
        // Merge hard mode prefs separately
        hardModePrefs: { ...currentPrefs, ...newPrefs },
        // Ensure hideInstructions is included
        hideInstructions: settings.hideInstructions !== undefined ? settings.hideInstructions : currentSettings.hideInstructions,
      };

      // Don't save the top-level isHardMode anymore
      delete mergedSettings.isHardMode;

      localStorage.setItem('squdle-settings', JSON.stringify(mergedSettings));
      // console.log('[StorageManager.saveSettings] Settings saved:', mergedSettings);
    } catch (error) {
      console.error('[StorageManager.saveSettings] Error saving settings:', error);
    }
  }

  /**
   * Loads game settings. Includes defaults for new settings.
   * @returns {object} - The loaded settings object with defaults.
   */
  loadSettings() {
    const defaults = {
      gridSize: 4,
      hardModePrefs: { '4': false, '5': false }, // Default prefs
      lastPlayedDate: null,
      hideInstructions: false, // Add default for hideInstructions
    };
    try {
      const settings = localStorage.getItem('squdle-settings');
      if (settings) {
        const parsed = JSON.parse(settings);
        // Ensure hardModePrefs exists and has defaults for both sizes
        parsed.hardModePrefs = {
          ...defaults.hardModePrefs, // Start with defaults
          ...(typeof parsed.hardModePrefs === 'object' && parsed.hardModePrefs !== null ? parsed.hardModePrefs : {}) // Merge saved prefs
        };
        // Remove legacy top-level isHardMode if present
        delete parsed.isHardMode;
        // Ensure hideInstructions exists, use default if not present in saved data
        parsed.hideInstructions = parsed.hideInstructions !== undefined ? parsed.hideInstructions : defaults.hideInstructions;

        return { ...defaults, ...parsed };
      }
      return defaults;
    } catch (error) {
      console.error('[StorageManager.loadSettings] Error loading settings:', error);
      return defaults; // Return defaults on error
    }
  }

  saveAccessibilitySettings(accessibilitySettings) {
     try {
       localStorage.setItem('squdle-accessibility', JSON.stringify(accessibilitySettings));
     } catch (error) {
       console.error('[StorageManager.saveAccessibilitySettings] Error saving accessibility settings:', error);
     }
   }

   loadAccessibilitySettings() {
     try {
       const settings = localStorage.getItem('squdle-accessibility');
       return settings ? JSON.parse(settings) : {};
     } catch (error) {
       console.error('[StorageManager.loadAccessibilitySettings] Error loading accessibility settings:', error);
       return {};
     }
   }

  /**
   * Saves the player stats object for a specific grid size to localStorage.
   * @param {object} stats - The stats object to save.
   * @param {number} gridSize - The grid size (4 or 5).
   * @returns {boolean} True if saving was successful, false otherwise.
   */
  savePlayerStats(stats, gridSize) {
    const statsKey = `squdle-player-stats-${gridSize}x${gridSize}`;
    // --- START ENHANCED LOGGING ---
    console.log(`[StorageManager.savePlayerStats ENTRY] Attempting to save to key: ${statsKey}`);
    // --- END ENHANCED LOGGING ---
    try {
      // Ensure we are saving a valid object
      if (typeof stats === 'object' && stats !== null) {
        const statsJson = JSON.stringify(stats); // Convert to JSON string
        // --- START ENHANCED LOGGING ---
        console.log(`[StorageManager.savePlayerStats] Data to save for key ${statsKey}:`, statsJson); // Log the data being saved
        // --- END ENHANCED LOGGING ---
        localStorage.setItem(statsKey, statsJson); // Save the string
        console.log(`[StorageManager.savePlayerStats EXIT] Successfully saved data for key ${statsKey}.`);
        return true; // Indicate success
      } else {
        console.error(`[StorageManager.savePlayerStats EXIT] Attempted to save invalid stats data (not an object or null) for ${gridSize}x${gridSize}. Data:`, stats);
        return false; // Indicate failure
      }
    } catch (error) {
      console.error(`[StorageManager.savePlayerStats EXIT] Error saving player stats for ${gridSize}x${gridSize}:`, error);
      return false; // Indicate failure
    }
  }


  loadPlayerStats(gridSize) {
    const statsKey = `squdle-player-stats-${gridSize}x${gridSize}`;
    // console.log(`[StorageManager.loadPlayerStats] Attempting to load key: ${statsKey}`);
    try {
      const statsJson = localStorage.getItem(statsKey); // Gets string from LS
      // console.log(`[StorageManager.loadPlayerStats] Raw data from localStorage for key "${statsKey}":`, statsJson ? statsJson.substring(0,100)+'...' : null);

      if (statsJson) {
        const stats = JSON.parse(statsJson); // Parses string to object
        // console.log(`[StorageManager.loadPlayerStats] Parsed data:`, stats);

        // Basic validation: Check if it's an object
        if (typeof stats === 'object' && stats !== null) {
          // console.log(`[StorageManager.loadPlayerStats] Data is valid object. Returning parsed stats.`);
          return stats; // Return the parsed object
        } else {
          console.warn(`[StorageManager.loadPlayerStats] Invalid stats data found (not an object or null) for ${gridSize}x${gridSize}. Data:`, statsJson);
          localStorage.removeItem(statsKey); // Remove invalid data
          return null;
        }
      }
      // console.log(`[StorageManager.loadPlayerStats] No stats found in localStorage for key "${statsKey}".`);
      return null; // No stats found
    } catch (error) {
      console.error(`[StorageManager.loadPlayerStats] Error loading/parsing player stats for ${gridSize}x${gridSize}:`, error);
      // Attempt to remove potentially corrupted key
      try { localStorage.removeItem(statsKey); } catch (removeError) { console.error(`Failed to remove potentially corrupted key ${statsKey}:`, removeError); }
      return null; // Return null on error
    }
  }

  resetPlayerStats() {
    console.log('[StorageManager.resetPlayerStats] Resetting stats...');
    let resetSuccess = true;
    for (let size = 4; size <= 5; size++) { // Iterate through supported sizes
      const statsKey = `squdle-player-stats-${size}x${size}`;
      try {
        localStorage.removeItem(statsKey);
        console.log(`Removed key: ${statsKey}`);
      } catch (error) {
        console.error(`Error removing stats key ${statsKey}:`, error);
        resetSuccess = false;
      }
    }
    // Also remove the legacy key if it exists
    const legacyKey = 'squdle-player-stats';
    if (localStorage.getItem(legacyKey)) {
      try {
        localStorage.removeItem(legacyKey);
        console.log(`Removed legacy key: ${legacyKey}`);
      } catch (error) {
        console.error(`Error removing legacy stats key ${legacyKey}:`, error);
        resetSuccess = false;
      }
    }
    // Removed reliance on window.gamePlayerStats - this should be handled in game.js
    // if (window.gamePlayerStats && typeof window.gamePlayerStats.resetStatsInMemory === 'function') {
    //   window.gamePlayerStats.resetStatsInMemory();
    // }

    return resetSuccess;
  }

  logStorageState() {
     console.log('[StorageManager] Current localStorage state for Squdle:');
     let count = 0;
     const keys = [];
     for (let i = 0; i < localStorage.length; i++) {
       const key = localStorage.key(i);
       if (key && key.startsWith('squdle-')) {
         keys.push(key);
       }
     }
     // Sort keys for consistent logging order
     keys.sort();

     for (const key of keys) {
       count++;
       try {
         let value = localStorage.getItem(key);
         if (value && value.length > 250) { // Increased truncation limit slightly
           value = value.substring(0, 250) + '... [truncated]';
         }
         console.log(`  - ${key}:`, value);
       } catch (e) {
         console.log(`  - ${key}: [Error reading value]`);
       }
     }
     if (count === 0) {
         console.log('  (No Squdle items found)');
     }
   }

} // End class
// Removed window export
/* END OF FILE js/StorageManager.js */
